import { notEmpty } from '@mahawi/eshop-common/dist/src/not-empty';
import {
  type IProductProperty,
  type ITranslation,
} from '@mahawi/eshop-common/dist/src/types';
import { type ILanguageState } from 'customer/react/reducers/language/types';
import { type IProductState } from 'customer/react/reducers/product/types';
import React, { type ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

function Properties({
  Product,
  Language,
}: {
  Product: IProductState;
  Language: ILanguageState;
}): React.JSX.Element {
  const { t } = useTranslation();

  const getRenderName = useCallback(
    (property: IProductProperty): string => {
      const name: ITranslation | undefined = property.names.find(
        (n: ITranslation): boolean => n.code === Language.languageType.code,
      );
      let renderName: string = name ? name.value : property.code;
      if (property.unit) {
        renderName += ` [${property.unit}]`;
      }
      return renderName;
    },
    [Language.languageType.code],
  );

  const getValue = useCallback(
    (property: IProductProperty): string | undefined => {
      const propertyValueName: ITranslation | undefined =
        property.propertyValue?.names?.find(
          (n: ITranslation): boolean => n.code === Language.languageType.code,
        );
      const value: string | undefined = propertyValueName
        ? propertyValueName.value
        : property.value;
      if (!value) {
        return undefined;
      }
      return property.maxValue ? `${value} – ${property.maxValue}` : value;
    },
    [Language.languageType.code],
  );

  const rows = useMemo((): ReactElement[] | null => {
    if (
      !Product.product ||
      !Product.product.productProperties ||
      Product.product.productProperties.length === 0
    ) {
      return null;
    }
    return Product.product.productProperties
      .map((productProperty: IProductProperty): ReactElement | null => {
        const renderName: string = getRenderName(productProperty);
        const value: string | undefined = getValue(productProperty);

        if (!value) {
          return null;
        }

        return (
          <tr key={productProperty.code}>
            <td>{renderName}</td>
            <td>{value}</td>
          </tr>
        );
      })
      .filter(notEmpty);
  }, [Product.product, getRenderName, getValue]);

  if (!rows) {
    return <div className="product properties" />;
  }

  return (
    <div className="product properties">
      <h2 id="properties-table-title">{t('product.properties')}</h2>
      <table aria-labelledby="properties-table-title">
        <thead>
          <tr>
            <th scope="col">{t('product.propertyName')}</th>
            <th scope="col">{t('product.propertyValue')}</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

const mapStateToProps = ({
  Product,
  Language,
}: {
  Product: IProductState;
  Language: ILanguageState;
}): {
  Product: IProductState;
  Language: ILanguageState;
} => ({ Product, Language });

export default connect(mapStateToProps)(Properties);
